/**
 * RecordingUtils — shared operational layer for tools that record media.
 *
 * Three functions that EVERY long-recording tool wants but previously
 * implemented inline:
 *
 *   1. acquireWakeLock(slug)   — keep the screen on while recording so OS
 *                                power-save doesn't dim the display and let
 *                                Chrome reclaim the hardware encoder.
 *                                Re-acquires on visibilitychange.
 *
 *   2. probeQuota(slug, opts)  — periodic OPFS / origin-storage quota check
 *                                with 80% / 95% warning callbacks. Stops
 *                                when controller.stop() is called.
 *
 *   3. wireVisibility(slug, …) — tab-backgrounded warning. Useful because
 *                                Chrome can pause encode work when a tab
 *                                is hidden (tied to "Codec reclaimed due
 *                                to inactivity" telemetry cluster).
 *
 * Telemetry: every helper emits standard `feature_use{slug, f:<name>}`
 * events through window.__ae. Names share a `record_*` / `recording_*`
 * prefix so cross-tool dashboards work. Caller passes the tool slug and
 * gets the events tagged automatically.
 *
 * Usage (dictaphone-shape):
 *
 *   var wl  = await window.RecordingUtils.acquireWakeLock('dictaphone');
 *   var qg  = window.RecordingUtils.probeQuota('dictaphone', {
 *       onWarning: function(level, info) {
 *           if (level === 'quota_low') toast(L.diskLow, 'warning');
 *           if (level === 'quota_critical') toast(L.diskCritical, 'error');
 *       }
 *   });
 *   var vis = window.RecordingUtils.wireVisibility('dictaphone',
 *       function() { toast(L.tabBackgrounded, 'info'); }
 *   );
 *
 *   // ... recording happens ...
 *
 *   await wl.release();
 *   qg.stop();
 *   vis.stop();
 *
 * Lifted from screen-recorder.recorder.js (commits e3052051a + 1416a4bfe)
 * after the OPFS-streaming rewrite proved the pattern. interview-studio
 * has its own (different) IDB-streaming + quota probe — left alone since
 * it's better than this for its specific use case (tab-crash recovery).
 */
(function (w) {
    'use strict';
    if (w.RecordingUtils) return;

    function emit(name, payload) {
        try {
            if (w.__ae) w.__ae('feature_use', Object.assign({ f: name }, payload || {}));
        } catch (_) {}
    }

    // ── Wake Lock ────────────────────────────────────────────────────────
    function acquireWakeLock(slug) {
        if (typeof navigator === 'undefined' || !navigator.wakeLock ||
            typeof navigator.wakeLock.request !== 'function') {
            emit('record_wake_lock_unsupported', { slug: slug });
            return Promise.resolve({ release: function () { return Promise.resolve(); } });
        }
        var sentinel = null;
        var manuallyReleased = false;
        var visListener = null;

        function reacquireOnVisible() {
            if (manuallyReleased || sentinel) return;
            if (document.visibilityState !== 'visible') return;
            navigator.wakeLock.request('screen').then(function (s) {
                sentinel = s;
                emit('record_wake_lock_reacquired', { slug: slug });
                s.addEventListener('release', function () { sentinel = null; });
            }).catch(function () {});
        }

        return navigator.wakeLock.request('screen').then(function (s) {
            sentinel = s;
            emit('record_wake_lock_acquired', { slug: slug });
            s.addEventListener('release', function () { sentinel = null; });
            visListener = function () { reacquireOnVisible(); };
            document.addEventListener('visibilitychange', visListener);
            return {
                release: function () {
                    manuallyReleased = true;
                    if (visListener) {
                        document.removeEventListener('visibilitychange', visListener);
                        visListener = null;
                    }
                    if (!sentinel) return Promise.resolve();
                    return sentinel.release().then(function () {
                        sentinel = null;
                        emit('record_wake_lock_released', { slug: slug });
                    }).catch(function () {});
                }
            };
        }).catch(function (e) {
            emit('record_wake_lock_failed', { slug: slug, msg: (e && e.message || '').slice(0, 80) });
            return { release: function () { return Promise.resolve(); } };
        });
    }

    // ── Quota probe ──────────────────────────────────────────────────────
    // opts.onWarning(level, info) where level ∈ 'quota_low_at_start' |
    //   'quota_low' (80%) | 'quota_critical' (95%)
    // opts.intervalMs — default 30_000
    // opts.minFreeBytes — bytes considered "low" at start, default 500 MB
    function probeQuota(slug, opts) {
        opts = opts || {};
        var intervalMs = opts.intervalMs || 30000;
        var minFreeBytes = opts.minFreeBytes != null ? opts.minFreeBytes : 500 * 1024 * 1024;
        var onWarning = typeof opts.onWarning === 'function' ? opts.onWarning : null;

        var hasApi = typeof navigator !== 'undefined' && navigator.storage &&
                     typeof navigator.storage.estimate === 'function';
        if (!hasApi) {
            emit('record_quota_unsupported', { slug: slug });
            return { stop: function () {}, currentEstimate: function () { return Promise.resolve(null); } };
        }

        function readEstimate() {
            return window.__storageEstimate().then(function (est) {
                var quota = est.quota || 0;
                var usage = est.usage || 0;
                return {
                    free: Math.max(0, quota - usage),
                    quota: quota,
                    usage: usage,
                    usagePct: quota > 0 ? usage / quota : 0
                };
            }).catch(function () { return null; });
        }

        // One-shot start probe — surfaces "low free space at start".
        readEstimate().then(function (e) {
            if (!e) return;
            if (e.free < minFreeBytes) {
                emit('record_quota_low_at_start', {
                    slug: slug,
                    freeMb: Math.round(e.free / 1024 / 1024),
                    quotaMb: Math.round(e.quota / 1024 / 1024)
                });
                if (onWarning) try { onWarning('quota_low_at_start', e); } catch (_) {}
            }
        });

        var warned80 = false, warned95 = false, stopped = false;
        var iv = setInterval(async function () {
            if (stopped) return;
            var e = await readEstimate();
            if (!e) return;
            if (e.usagePct >= 0.95 && !warned95) {
                warned95 = true;
                emit('record_quota_critical', {
                    slug: slug, freeMb: Math.round(e.free / 1024 / 1024), usagePct: Math.round(e.usagePct * 100)
                });
                if (onWarning) try { onWarning('quota_critical', e); } catch (_) {}
            } else if (e.usagePct >= 0.8 && !warned80) {
                warned80 = true;
                emit('record_quota_low', {
                    slug: slug, freeMb: Math.round(e.free / 1024 / 1024), usagePct: Math.round(e.usagePct * 100)
                });
                if (onWarning) try { onWarning('quota_low', e); } catch (_) {}
            }
        }, intervalMs);

        return {
            stop: function () { stopped = true; clearInterval(iv); },
            currentEstimate: readEstimate
        };
    }

    // ── Visibility wiring ────────────────────────────────────────────────
    function wireVisibility(slug, onHidden, onVisible) {
        var listener = function () {
            if (document.visibilityState === 'hidden') {
                emit('record_visibility_hidden', { slug: slug });
                if (onHidden) try { onHidden(); } catch (_) {}
            } else if (document.visibilityState === 'visible') {
                if (onVisible) try { onVisible(); } catch (_) {}
            }
        };
        document.addEventListener('visibilitychange', listener);
        return {
            stop: function () { document.removeEventListener('visibilitychange', listener); }
        };
    }

    // ── Silence monitor ──────────────────────────────────────────────────
    // Detects audio that goes silent on an ALIVE track mid-recording — the
    // most common real-world audio failure (Дмитрий #738: 3h lecture, audio
    // dropped at 35s, track stayed live, encoder never errored, NO telemetry).
    // track.onended / encoder.error do NOT fire for this; only level analysis
    // catches it.
    //
    // Algorithm — time-domain RMS + peak dual gate:
    //   • RMS  ≥ -50 dBFS (0.00316) ⇒ steady signal present
    //   • peak ≥ -35 dBFS (0.0178)  ⇒ transient present (note onset, breath,
    //                                  syllable) — preserves detection through
    //                                  pianissimo / dramatic-pause / low-mic
    //                                  passages that have peaks but low RMS.
    //   hasSignal = RMS-gate OR peak-gate. A single non-silent sample resets
    //   the silence timer. After `graceMs` of confirmed signal the monitor
    //   ARMS; once armed, `silentThresholdMs` with NO signal fires once.
    //
    // Why time-domain not frequency-domain: a steady real signal's per-bin
    // 10th-percentile IS the signal level (ai-music-cleaner removed a
    // spectral-percentile gate after it damaged clean inputs 4-9 dB).
    // Why fixed -50 dBFS not adaptive: adaptive floor HIDES the "mic was
    // always quiet" case (audio-comparator uses adaptive only for unknown
    // arbitrary files; a controlled mixer bus wants a fixed floor).
    // Why setTimeout not rAF: rAF pauses when the tab is backgrounded — the
    // exact moment audio failures are most likely (interview-studio lesson).
    //
    // opts:
    //   isRunning()         — poll continues while truthy (required)
    //   isPaused()          — when truthy, skip the read AND refresh both
    //                         timers + disarm (pause is a mini audio restart;
    //                         without this, a >threshold pause false-fires on
    //                         resume before warm-up audio arrives)
    //   userWantedAudio     — if false, monitor is a no-op (don't flag a
    //                         recording the user never asked audio for)
    //   onSilenceConfirmed(payload)  — fires ONCE when silence is confirmed
    //   onRecover()                  — fires when signal returns after a
    //                                  confirmed silence (clears live banner)
    //   getContext()        — () => AudioContext, for ctxState telemetry
    //   getTracks()         — () => [MediaStreamTrack], for readyState telemetry
    //   graceMs (2000), silentThresholdMs (15000), pollMs (500),
    //   rmsThreshold (0.00316), peakThreshold (0.0178)
    //
    // returns { stop() } — call from pipeline stop()/abort().
    function attachSilenceMonitor(slug, mixer, opts) {
        opts = opts || {};
        if (!mixer || !mixer.analyser || !opts.userWantedAudio || typeof opts.isRunning !== 'function') {
            return { stop: function () {} };
        }
        var graceMs = opts.graceMs || 2000;
        // 15 s, not 5 s. dictaphone learned (dictaphone.blade.php:1196-1198)
        // that a 5 s silence threshold "caused rage-quits from false alarms" —
        // natural lecture/screencast pauses (reading a slide, a beat of
        // silence) are routinely >5 s and are NOT lost audio. screen-recorder
        // shipped 5 s and hit the same wall: 271 silence_mid / 126 sessions in
        // 7 d, 91 of which RECOVERED (= the audio came back, so it was a pause,
        // not a loss). 15 s waits out ordinary pauses; a genuinely dead source
        // (Дмитрий) stays silent well past 15 s and still fires.
        var silentThresholdMs = opts.silentThresholdMs || 15000;
        var pollMs = opts.pollMs || 500;
        var rmsThreshold = opts.rmsThreshold || 0.00316;   // -50 dBFS
        var peakThreshold = opts.peakThreshold || 0.0178;  // -35 dBFS
        var analyser = mixer.analyser;
        // getFloatTimeDomainData writes up to fftSize samples (the raw
        // waveform), NOT frequencyBinCount (= fftSize/2, the FFT bin count).
        // Using frequencyBinCount silently analyses only half the window.
        var bufLen = analyser.fftSize;
        var buf = new Float32Array(bufLen);
        var lastNonSilent = 0;
        var armed = false;
        var armedAt = 0;
        var started = false;
        var stopped = false;
        // Warn-ONCE per recording (dictaphone's `_silenceWarned` model). The
        // old code toggled silenceFired true→false on every silence↔signal
        // transition, so flapping audio (a lecture pausing between sentences)
        // re-fired recover endlessly — 2034 recovered_live events across 91
        // sessions in 7 d (max 287 in ONE session). Now: warn once, recover
        // at most once, then stay quiet. The sticky result flag still records
        // that the recording had a silent stretch.
        var warned = false;
        var recovered = false;

        function silenceDispatch(now) {
            var ctx = opts.getContext ? opts.getContext() : null;
            var tracks = opts.getTracks ? (opts.getTracks() || []) : [];
            var payload = {
                slug: slug,
                hadAudioForMs: Math.round(lastNonSilent - armedAt),
                thresholdMs: silentThresholdMs,
                ctxState: ctx ? ctx.state : 'none',
                tracks: tracks.map(function (t) {
                    return { ready: t.readyState, muted: t.muted, enabled: t.enabled };
                })
            };
            if (opts.onSilenceConfirmed) try { opts.onSilenceConfirmed(payload); } catch (_) {}
        }

        (async function loop() {
            // performance.now baseline established on first tick so we don't
            // count construction-to-first-poll as elapsed time.
            while (opts.isRunning() && !stopped) {
                await new Promise(function (r) { setTimeout(r, pollMs); });
                if (!opts.isRunning() || stopped) break;
                var now = (typeof performance !== 'undefined' ? performance.now() : 0);
                if (!started) { started = true; armedAt = now; lastNonSilent = now; continue; }
                if (opts.isPaused && opts.isPaused()) {
                    // Pause = mini audio restart. Refresh both timers + disarm
                    // so the post-resume warm-up gets the same grace as start.
                    // Without this, a >silentThresholdMs pause fires within one
                    // poll of resume (false positive that floods telemetry +
                    // erodes trust in the real signal).
                    lastNonSilent = now; armedAt = now; armed = false;
                    continue;
                }
                try { analyser.getFloatTimeDomainData(buf); }
                catch (_) { continue; }
                var sum = 0, peakAbs = 0;
                for (var i = 0; i < bufLen; i++) {
                    var v = buf[i];
                    var a = v < 0 ? -v : v;
                    sum += v * v;
                    if (a > peakAbs) peakAbs = a;
                }
                var rms = Math.sqrt(sum / bufLen);
                var hasSignal = rms >= rmsThreshold || peakAbs >= peakThreshold;
                if (hasSignal) {
                    lastNonSilent = now;
                    if (!armed && (now - armedAt) > graceMs) armed = true;
                    // Signal returned after THE one warning → recover ONCE,
                    // then never again (no flap spam).
                    if (warned && !recovered) {
                        recovered = true;
                        if (opts.onRecover) try { opts.onRecover(); } catch (_) {}
                    }
                } else if (armed && !warned && (now - lastNonSilent) > silentThresholdMs) {
                    warned = true;
                    silenceDispatch(now);
                }
            }
        })();

        return { stop: function () { stopped = true; } };
    }

    // ── AudioContext state monitor ───────────────────────────────────────
    // The CAUSE half of silence detection: if the AudioContext is suspended
    // (OS audio-session preemption, mobile background) the analyser sees
    // nothing AND the encoder gets no samples → silent file, no error. Auto-
    // resume on suspend; flag if resume fails. Also checks the CURRENT state
    // once at attach — an already-suspended context never fires a transition
    // event, so the once-on-change handler alone would miss it.
    //
    // opts:
    //   isRunning()  — gate callbacks (required)
    //   isActive()   — () => recording-or-paused, gate resume attempts
    //   onResumeFail() — fires when resume() rejects (sets the tool's
    //                    _audioContextSuspended flag for the post-stop banner)
    //   onTelemetry(name, payload)
    // returns { stop() }
    function attachAudioCtxMonitor(slug, ctx, opts) {
        opts = opts || {};
        if (!ctx || typeof opts.isRunning !== 'function') return { stop: function () {} };
        var stopped = false;
        function tel(name, payload) {
            if (opts.onTelemetry) try { opts.onTelemetry(name, Object.assign({ slug: slug }, payload || {})); } catch (_) {}
        }
        function handleSuspended() {
            if (stopped || !opts.isRunning()) return;
            if (!(opts.isActive ? opts.isActive() : true)) return;
            ctx.resume().then(function () {
                tel('record_audio_ctx_resumed', {});
            }).catch(function (e) {
                if (opts.onResumeFail) try { opts.onResumeFail(); } catch (_) {}
                tel('record_audio_ctx_resume_failed', { msg: (e && e.message || '').slice(0, 80) });
            });
        }
        var listener = function () {
            if (stopped || !opts.isRunning()) return;
            tel('record_audio_ctx_state', { state: ctx.state });
            if (ctx.state === 'suspended') handleSuspended();
        };
        ctx.addEventListener ? ctx.addEventListener('statechange', listener) : (ctx.onstatechange = listener);
        // Catch an already-suspended context that will never emit a transition.
        if (ctx.state === 'suspended') handleSuspended();
        return {
            stop: function () {
                stopped = true;
                try { ctx.removeEventListener && ctx.removeEventListener('statechange', listener); } catch (_) {}
            }
        };
    }

    w.RecordingUtils = {
        acquireWakeLock: acquireWakeLock,
        probeQuota: probeQuota,
        wireVisibility: wireVisibility,
        attachSilenceMonitor: attachSilenceMonitor,
        attachAudioCtxMonitor: attachAudioCtxMonitor
    };
})(window);
